<template>
  <div
    v-if="shouldShowPopup"
    class="subscription-popup"
  >
    <div ref="popupEl" class="subscription-popup__inner" :style="popupStyle">
      <NewsletterContent
        v-if="!isComplete"
        :blok="blok"
        :show-benefits="popupShowBenefits"
        :show-description="popupShowDescription"
        benefits-class="hidden-xs visible-grid-sm"
        :content-type="contentType"
      />

      <NewsletterForm @complete="onFormComplete" />

      <button
        class="button button--close rounded-sm"
        type="button"
        @click="closePopup(true)"
      >
        <Icon name="fa6-solid:xmark" size="21" />
        <span class="label">Dismiss</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BlockSubscriptionSettingsStoryblok } from '~/types/storyblok'
import { useElementSize, useWindowSize } from '@vueuse/core'
import { useTemplateRef } from 'vue'

const props = defineProps<{
  blok?: BlockSubscriptionSettingsStoryblok
  contentType?: string
}>()

const { isPopupDisabled, popupShowDescription, popupShowBenefits, popupStyle } =
  useNewsletterContent(props.blok)

const newsletterPopupStore = useNewsletterPopupStore()

const {
  isOpened,
  forceOpen,
  isBlockerOpened,
  allowPopupActivation,
  isDisabledForSubscriber,
  isDisabledForNonSubscriber,
} = storeToRefs(newsletterPopupStore)

const { closePopup, setFormCookie } = newsletterPopupStore

const shouldShowPopup = computed(() => {
  return !isPopupDisabled.value &&
    !isBlockerOpened.value &&
    isOpened.value &&
    allowPopupActivation.value &&
    ((!isDisabledForSubscriber.value && !isDisabledForNonSubscriber.value) || forceOpen.value)
})

// used to hide other text only
const isComplete = ref(false)
let delayActivation: ReturnType<typeof setTimeout>
let delayForMessage: ReturnType<typeof setTimeout>

const onFormComplete = function () {
  isComplete.value = true

  // set cookie for subscriber
  delayForMessage = setTimeout(() => {
    setFormCookie(true)
  }, 5000)
}

onMounted(() => {
  allowPopupActivation.value = false
  delayActivation = setTimeout(() => {
    allowPopupActivation.value = true
  }, 1500)
})

onUnmounted(() => {
  clearTimeout(delayActivation)
  clearTimeout(delayForMessage)
  allowPopupActivation.value = false
  closePopup()
})

// calulate size
const el = useTemplateRef('popupEl')
const { height: elementHeight } = useElementSize(el)
const { width: windowWidth } = useWindowSize()

const root = document?.documentElement

// Watch for changes to the blok prop and reset expanded state
watch(
  () => [isOpened.value, windowWidth.value],
  () => {
    if (isOpened.value) {
      setTimeout(() => {
        // set root CSS var
        root.style.setProperty(
          '--subscription-popup-h',
          `clamp(240px, 50vh, ${Math.round(elementHeight.value)}px)`,
        )
      }, 500)
    }
  },
  { immediate: true },
)
</script>

<style lang="less" scoped>
.subscription-popup {
  .fixed();
  .animate-slideUp();

  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 10005;
  min-width: @320px;
  max-width: @screen-xwide;
  width: 100vw;

  @media @tablet {
    padding: 0 @gutter;
  }

  @media print {
    .hidden();
  }

  .subscription-popup__inner {
    .relative();
    .round(18);

    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    width: 100%;
    max-height: 100%;
    padding: @gutter-xl @gutter-lg @gutter-xl;
    background-color: @color-fill-text;
    background-size: 0;
    background-repeat: no-repeat;
    color: @color-text-reverse;

    min-height: var(--subscription-popup-h);

    @media @tablet {
      padding: @gutter-xl @gutter-xl @gutter-xl;
      padding-right: 40%;
      background-position: 66% 100%;
      background-size: auto 100%;
    }

    @media @desktop {
      background-position: 100% 50%;
    }

    @media @wide {
      padding-left: @gutter-xxl;
      padding-right: 30%;
    }

    button.button--close {
      .absolute();
      color: @color-text-reverse;
      opacity: 0.9;
      right: 6px;
      top: 3px;

      @media @tablet {
        right: @gutter-sm;
        top: @gutter-sm;
      }

      &:hover,
      &:active {
        color: @color-text-reverse;
        opacity: 1;
      }

      span.label {
        .font-size(13);
        margin: 0 @4px;
        order: -1;

        @media @tablet {
          .font-xxs();
          margin: 0 @6px;
          order: 1;
        }
      }
    }
  }
}
</style>
